import { datadogRum } from '@datadog/browser-rum';
import { AxiosRequestConfig } from 'axios';
import { reactive } from 'vue';

import { cleanDoc, formatDateDto } from '@/helpers/formatters';
import generateToken from '@/helpers/generateToken';
import { versoes } from '@/helpers/versionamento';

import { CriarUsuario } from '@/models/CriarUsuario';
import SSOTokenSemCadastroDto from '@/models/dto/SSOTokenSemCadastroDto';
import SSOValidarCpfCnpjDto from '@/models/dto/SSOValidarCpfCnpjDto';
import { DeeplinkType } from '@/models/enums';
import JwtAuthToken from '@/models/JwtAuthToken';
import SSOTokenSemCadastro from '@/models/resources/SSOTokenSemCadastro';

import adoptlyService from './AdoptlyService';
import HttpService from './HttpService';
import queryStringService from './QueryStringService';
import SessionService from './SessionService';
import storageService from './StorageService';
import ThemeService from './ThemeService';
import { ApiResponse } from '@/models/resources/ApiResponse';

interface TokenResponse {
  accessToken: string;
  dataCriacao: string;
  dataValidade: string;
  versaoPoliticaPrivacidade: string;
  versaoTermoUso: string;
}

type TokenResponseDocumentConfirm = Omit<TokenResponse, 'accessToken'> & {
  accessToken: string | null;
  confirmarDocumento?: boolean;
  primeiroNome?: string;
};

interface IConsultaCadastro {
  hash: string,
  cadastrado: boolean,
}

const versoesDeTermosDto = {
  versaoPoliticaPrivacidade: versoes.politicaPrivacidade,
  versaoTermoUso: versoes.termoUso,
};

function saveToken<T extends TokenResponse>(resp: ApiResponse<T>) {
  const jwtAuthToken = new JwtAuthToken(resp.data.accessToken);
  storageService.session.set(jwtAuthToken);
  if (jwtAuthToken){
    storageService.sessionSS.set(jwtAuthToken);
  }
  adoptlyService.init(jwtAuthToken);
  return resp.data;
}

function makeClientTokenHeaders(document: string) {
  return {
    'Client-Token': generateToken(document),
  };
}

class AuthService extends HttpService {
  validSendEvent = false;

  login(
    login: string,
    dataNascimento: Date,
    tokenRecaptcha: string,
  ): Promise<TokenResponse> {
    datadogRum.addAction('sys:auth_service:login');
    const payload = {
      login: cleanDoc(login),
      dataNascimento: formatDateDto(dataNascimento),
      tokenRecaptcha,
      queryString: queryStringService.getFromStorage(),
      tema: ThemeService.getCurrentTheme(),
    };

    const config: AxiosRequestConfig = {
      headers: makeClientTokenHeaders(login),
    };

    return this.post<ApiResponse<TokenResponse>>('/api/portal/Autenticacao/Login', payload, config)
      .then(saveToken);
  }

  async googleLogin(
    cpf: string,
    tokenGoogle: string,
    tokenRecaptcha: string,
  ): Promise<TokenResponse> {
    datadogRum.addAction('sys:auth_service:google_login');
    const payload = {
      cpf: cleanDoc(cpf),
      token: tokenGoogle,
      tokenRecaptcha,
      queryString: queryStringService.getFromStorage(),
    };

    const config: AxiosRequestConfig = {
      headers: makeClientTokenHeaders(cpf),
    };

    return this.post<ApiResponse<TokenResponse>>('/api/portal/Google/Login', payload, config)
      .then(saveToken);
  }

  consultarCadastro(login: string, tokenRecaptcha: string): Promise<IConsultaCadastro> {
    datadogRum.addAction('sys:auth_service:consultar_cadastro');

    const utmSource = queryStringService.getFromStorage().utm_source;
    const payload = { login: cleanDoc(login), tokenRecaptcha, utmSource };

    const config: AxiosRequestConfig = {
      headers: makeClientTokenHeaders(login),
    };

    return this.post<ApiResponse<IConsultaCadastro>>('/api/portal/Cadastro/Consulta', payload, config)
      .then((resp) => resp.data);
  }

  cadastrar(userData: CriarUsuario, tokenRecaptcha: string): Promise<boolean> {
    datadogRum.addAction('sys:auth_service:cadastrar');
    const usuario = {
      ...userData,
      dataNascimento: formatDateDto(userData.dataNascimento),
      ...versoesDeTermosDto,
    };

    const config: AxiosRequestConfig = {};

    if (usuario.login) {
      usuario.login = cleanDoc(usuario.login);
      config.headers = makeClientTokenHeaders(usuario.login);
    }

    const payload = {
      usuario,
      tokenRecaptcha,
      queryString: queryStringService.getFromStorage(),
      tema: ThemeService.getCurrentTheme(),
    };

    return this.post<ApiResponse<boolean>>('/api/portal/Cadastro/CadastroV1', payload, config)
      .then((resp) => resp.data);
  }

  cadastrarComGoogle(cpf: string, tokenGoogle: string): Promise<boolean> {
    datadogRum.addAction('sys:auth_service:cadastrar_com_google');
    const payload = { cpf: cleanDoc(cpf), token: tokenGoogle };
    return this.post<ApiResponse<boolean>>('/api/portal/Google/CadastroV1', payload)
      .then((resp) => resp.data);
  }

  cadastrarComDeeplink(dados: {
    token: string,
    dataNascimento: Date,
    telefoneCelular?: string,
    email?: string,
    typeId?: string,
    id?: string,
  }): Promise<{ accessToken: string }> {
    datadogRum.addAction('sys:auth_service:cadastrar_com_deeplink');
    const { typeId, id, ...rest } = dados;
    
    const queryString: Record<string, string> = {};
    if (typeId) queryString.typeId = typeId;
    if (id) queryString.id = id;

    // Determina qual tipo de contato está sendo validado
    const tipoContatoAValidar = dados.email ? 'e' : 't';

    const payload = {
      ...rest,
      dataNascimento: formatDateDto(dados.dataNascimento),
      tipoContatoAValidar,
      ...versoesDeTermosDto,
      ...(Object.keys(queryString).length > 0 ? { queryString } : {}),
    };

    return this.post<ApiResponse<{ accessToken: string }>>('/api/portal/Cadastro/DeeplinkCaptacaoEmail', payload)
      .then((resp) => resp.data);
  }

  finalizarCadastroComGoogle(
    cpf: string,
    tokenGoogle: string,
    telefoneCelular: string,
    optIn = true,
  ): Promise<boolean> {
    datadogRum.addAction('sys:auth_service:finalizar_cadastro_com_google');
    const payload = {
      cpf: cleanDoc(cpf),
      token: tokenGoogle,
      telefoneCelular,
      optIn,
      queryString: queryStringService.getFromStorage(),
      ...versoesDeTermosDto,
    };

    return this.post<ApiResponse<boolean>>('/api/portal/Google/Confirmar', payload)
      .then((resp) => resp.data);
  }

  renovarToken(): Promise<TokenResponse> {
    return this.get<ApiResponse<TokenResponse>>('/api/portal/Autenticacao/Token')
      .then(saveToken);
  }

  confirmarEmail(token: string): Promise<TokenResponse> {
    datadogRum.addAction('sys:auth_service:confirmar_email');
    const payload = {
      token,
      queryString: queryStringService.getFromStorage(),
    };

    return this.post<ApiResponse<TokenResponse>>('/api/Portal/Cadastro/ConfirmarEmail', payload)
      .then(saveToken);
  }

  autenticarToken(
    token: string,
    deeplinkType: DeeplinkType = DeeplinkType.AREA_LOGADA,
  ): Promise<TokenResponseDocumentConfirm> {
    datadogRum.addAction('sys:auth_service:autenticar_token');
    const payload = {
      token,
      queryString: queryStringService.getFromStorage(),
      tipo: deeplinkType,
    };

    return this.post<ApiResponse<TokenResponseDocumentConfirm>>('/api/Portal/SimpleSignOn', payload)
      .then((resp) => {
        if (resp.data.accessToken) {
          return saveToken(resp as ApiResponse<TokenResponseDocumentConfirm & { accessToken: string }>);
        }

        return resp.data;
      });
  }

  /** Valida token do deeplink sem cadastro (SimpleSignOn/ValidarToken).
   *
   * @param token         Token de segurança com informações do usuário
   * @param dataValidade  Data de validade para acesso do deeplink sem cadastro
   * @param extras        `typeId` e `id` opcionais vindos da query do deeplink
   */
  ssoValidarToken(
    token: string,
    dataValidade: string,
    queryString?: { typeId?: string; id?: string },
  ): Promise<SSOTokenSemCadastro> {
    const utmSource = queryStringService.getFromStorage().utm_source;
    const payload: {
      token: string;
      dataValidade: string;
      utmSource: string;
      queryString?: { typeId?: string; id?: string };
    } = { token, dataValidade, utmSource };

    if (queryString && Object.keys(queryString).length > 0) {
      payload.queryString = queryString;
    }

    return this.post<ApiResponse<SSOTokenSemCadastroDto>>('/api/portal/SimpleSignOn/ValidarToken', payload)
      .then((resp) => new SSOTokenSemCadastro(resp.data));
  }

  /** Verifica se os cinco dígitos do CPF ou CNPJ fornecidos pelo consumidor são válidos.
   *
   * @param token           Token do deeplink contido no link enviado ao consumidor
   * @param digitosCpfCnpj  Cinco primeiros dígitos do CPF/CNPJ fornecidos pelo consumidor
   * @param extras          `typeId` e `id` opcionais vindos da query do deeplink
   */
  ssoValidarCpfCnpj(
    token: string,
    digitosCpfCnpj: string,
    extras?: { typeId?: string; id?: string },
  ): Promise<SSOValidarCpfCnpjDto> {
    const queryString: Record<string, string> = {
      ...queryStringService.getFromStorage(),
      ...(extras?.typeId ? { typeId: extras.typeId } : {}),
      ...(extras?.id ? { id: extras.id } : {}),
    };

    const payload = {
      digitosCpfCnpj,
      token,
      queryString,
    };

    return this.post<ApiResponse<SSOValidarCpfCnpjDto>>(
      '/api/portal/SimpleSignOn/ValidarCpfCnpj',
      payload,
    ).then((resp) => resp.data);
  }

  acceptPolicies() {
    return this.post<ApiResponse<TokenResponse>>('/api/portal/TermosAceite/Assinar', versoesDeTermosDto)
      .then((resp) => {
        const jwtAuthToken = new JwtAuthToken(resp.data.accessToken);
        SessionService.login(jwtAuthToken);
      });
  }

  /**
   * Solicitar o envio de código OTP no email cadastro
   * @returns tempo de expiração restante do código OTP em segundos
   */
  solicitarOTP() {
    return this.get<ApiResponse<{tempoExpiracaoOtp: number}>>('/api/portal/Autenticacao/SolicitarOtp')
      .then((resp) => resp.data);
  }

  /**
   * Fazer validação do código OTP
   * @param codigoOtp código OTP inserido pelo usuário
   * @returns Token JWT com autenticação OTP
   */
  confirmarOTP(codigoOtp: string) {
    const payload = {
      codigoOtp,
    };

    return this.post<ApiResponse<TokenResponse>>('/api/portal/Autenticacao/ConfirmarOtp', payload)
      .then(saveToken);
  }
}

const authService = new AuthService();
export default reactive(authService);
